import { CellContext } from '@tanstack/react-table';
import { Users, XCircle } from 'lucide-react';

import {
  EnvironmentId,
  EnvironmentStatus,
  K8sDistributionType,
} from '@/react/portainer/environments/types';
import { useCancelOmniProvisioning } from '@/react/kubernetes/cluster/omni/queries/useCancelOmniProvisioning';
import { notifySuccess } from '@/portainer/services/notifications';

import { Button } from '@@/buttons';
import { Link } from '@@/Link';

import { EnvironmentListItem } from '../types';
import { confirmCancelProvisioning } from '../ConfirmCancelProvisioningModal';

import { columnHelper } from './helper';

export const actions = columnHelper.display({
  header: 'Actions',
  cell: Cell,
});

function Cell({
  row: { original: environment },
}: CellContext<EnvironmentListItem, unknown>) {
  const credentialId = environment.CloudProvider?.CredentialID;

  if (
    environment.Status === EnvironmentStatus.Provisioning &&
    environment.CloudProvider?.Provider === K8sDistributionType.OMNI &&
    credentialId
  ) {
    return (
      <CancelProvisioningButton
        environmentId={environment.Id}
        environmentName={environment.Name}
        credentialId={credentialId}
      />
    );
  }

  if (
    environment.Status === EnvironmentStatus.Provisioning ||
    environment.Status === EnvironmentStatus.Error
  ) {
    return <>-</>;
  }

  return (
    <Button
      as={Link}
      props={{
        to: 'portainer.endpoints.endpoint.access',
        params: { id: environment.Id },
      }}
      color="link"
      icon={Users}
      data-cy={`environment-manage-access-button-${environment.Name}`}
    >
      Manage access
    </Button>
  );
}

function CancelProvisioningButton({
  environmentId,
  environmentName,
  credentialId,
}: {
  environmentId: EnvironmentId;
  environmentName: string;
  credentialId: number;
}) {
  const cancelMutation = useCancelOmniProvisioning();

  return (
    <Button
      color="dangerlight"
      icon={XCircle}
      onClick={handleCancel}
      disabled={cancelMutation.isLoading}
      data-cy={`environment-cancel-provisioning-button-${environmentName}`}
    >
      Cancel
    </Button>
  );

  async function handleCancel() {
    const { deleteCluster } =
      (await confirmCancelProvisioning(environmentName)) || {};

    if (deleteCluster === undefined) {
      return;
    }

    cancelMutation.mutate(
      { credentialId, environmentId, deleteCluster },
      {
        onSuccess() {
          notifySuccess('Provisioning cancelled', environmentName);
        },
      }
    );
  }
}
